import type { UIViewInjectedProps } from '@uirouter/react';
import { observer } from 'mobx-react-lite';

import { api } from '@/api';
import { getAllowedFormFields } from '@/components/generic-form/utils';
import { DrawerFormWithNote } from '@/components/note-form';
import { PERMISSION_OBJ_TRANSFER } from '@/constants/permissions';
import { addressStore, campaignStore, counterpartyStore, productStore, transferStore } from '@/stores';
import { NoteObjectType } from '@/types/enums';
import { getTranslatedString } from '@/utils';
import { withoutRepeatValue } from '@/utils/controller';
import { useMassUpdateCashedStores } from '@/utils/store';

import { getFormFields } from './setup';

const entity = 'transfers';
const store = transferStore;

export const TransferForm = observer(({ transition }: UIViewInjectedProps) => {
  const isCreate = transition.router.globals.current.name === `base-layout.${entity}.create`;
  const { transferId: itemId } = transition.router.globals.params;
  const foundItem = store.items.find(({ id }: cyrian.api.transfers.Transfer) => id === +itemId);

  const { loading } = useMassUpdateCashedStores([counterpartyStore, productStore, campaignStore, addressStore]);

  const onClose = () => {
    transition.router.stateService.go(`base-layout.${entity}`);
  };

  const initValues =
    !isCreate && foundItem
      ? {
          ...foundItem,
          productId: foundItem.product?.id,
          campaignId: foundItem.campaign?.id,
          carrierId: foundItem.carrier?.id,
          transportChargeCounterpartyId: foundItem.transportChargeCounterparty?.id,
          loadingCounterpartyId: foundItem.loadingCounterparty?.id,
          startLocationId: foundItem.startLocation?.id,
          endLocationId: foundItem.endLocation?.id,
        }
      : {};

  const title = isCreate
    ? getTranslatedString('transfers.create-new')
    : getTranslatedString('transfers.transfer-number', { 0: itemId });

  const formFields = getFormFields();
  const allowedFields = getAllowedFormFields(formFields, isCreate, PERMISSION_OBJ_TRANSFER);

  const resourceController = withoutRepeatValue(initValues, {
    create: (values: any) => api[entity].create(values).source.then(({ data }: any) => store.addItem(data)),
    update: (values: any) =>
      api[entity].update(foundItem.id, values).source.then(({ data }: any) => store.updateItem(data)),
  });

  const layout = {
    labelCol: { span: 12 },
    wrapperCol: { span: 12 },
  };

  return (
    <DrawerFormWithNote
      {...layout}
      objectType={NoteObjectType.Transfer}
      resourceId={itemId}
      title={title}
      initValues={initValues}
      formFields={allowedFields}
      onClose={onClose}
      resourceController={resourceController}
      loaderCondition={loading || (!isCreate && !foundItem)}
    />
  );
});
